import React from "react";
import {
    UserOutlined,
    MailOutlined,
    PhoneOutlined,
    CalendarOutlined,
    InfoCircleOutlined,
    BankOutlined,
    DollarOutlined,
} from "@ant-design/icons";
import { Col, Row, Tag } from "antd";
import CommonCard from "../global/card";
import { cn } from "../../utils/cn";
import { formatDateTime } from "../../utils/functions";

const EnhancedAffiliatesCustomerDetails = ({
    customerDetail,
    setShowDetail
}) => {
    if (!customerDetail) return null;

    const getIconForField = (label) => {
        const iconMap = {
            Name: <UserOutlined />,
            Email: <MailOutlined />,
            Phone: <PhoneOutlined />,
            "Registered At": <CalendarOutlined />,
            "Plan Name": <BankOutlined />,
            "Plan Price": <DollarOutlined />,
            "Commission Amount": <DollarOutlined />,
            "Commission Type": <InfoCircleOutlined />,
            "Commission Value": <InfoCircleOutlined />,
            "Affiliate Name": <UserOutlined />,
            "Affiliate Email": <MailOutlined />,
            "Company Name": <BankOutlined />,
        };
        return iconMap[label] || <InfoCircleOutlined />;
    };

    return (
        <div className="p-6" style={{ backgroundColor: "#f5f7fa" }}>
            <CommonCard
                title={
                    <div className="flex items-center gap-2">
                        <UserOutlined style={{ color: "var(--primary-color, #1890ff)", fontSize: "18px" }} />
                        <span style={{ fontSize: "16px", fontWeight: 600 }}>Customer Details</span>
                    </div>
                }
            >
                <Row gutter={[24, 0]}>
                    {/* Customer/User Details Section */}
                    <Col span={24} className="border-b border-gray-200 py-4">
                        <div className="flex items-center gap-2 mb-4">
                            <UserOutlined style={{ color: "var(--primary-color, #1890ff)", fontSize: "16px" }} />
                            <span style={{ fontSize: "15px", fontWeight: 600 }}>Customer Information</span>
                        </div>
                        <Row gutter={[24, 16]}>
                            {[
                                {
                                    label: "Name",
                                    value: customerDetail?.User?.[0]?.name
                                },
                                { label: "Email", value: customerDetail?.User?.[0]?.email },
                                { label: "Phone", value: customerDetail?.User?.[0]?.phone },
                                { label: "Registered At", value: formatDateTime(customerDetail?.User?.[0]?.createdAt) },
                            ].map((item) => (
                                <Col span={12} key={item.label}>
                                    <div className="flex flex-col">
                                        <span className="text-xs text-gray-500 flex items-center gap-2 mb-1">
                                            <span style={{ color: "var(--primary-color, #1890ff)" }}>
                                                {getIconForField(item.label)}
                                            </span>
                                            {item.label}
                                        </span>
                                        <span className="text-sm font-semibold text-gray-900">{item.value || "—"}</span>
                                    </div>
                                </Col>
                            ))}
                        </Row>
                    </Col>

                    {/* Plan Details Section */}
                    <Col span={24} className="border-b border-gray-200 py-4">
                        <div className="flex items-center gap-2 mb-4">
                            <BankOutlined style={{ color: "var(--primary-color, #1890ff)", fontSize: "16px" }} />
                            <span style={{ fontSize: "15px", fontWeight: 600 }}>Plan Information</span>
                        </div>
                        <Row gutter={[24, 16]}>
                            {[
                                { label: "Plan Name", value: customerDetail?.Plan?.[0]?.planName },
                                { label: "Plan Price", value: `$${customerDetail?.Plan?.[0]?.planPrice}`, isTag: true },
                            ].map((item) => (
                                <Col span={12} key={item.label}>
                                    <div className="flex flex-col">
                                        <span className="text-xs text-gray-500 flex items-center gap-2 mb-1">
                                            <span style={{ color: "var(--primary-color, #1890ff)" }}>
                                                {getIconForField(item.label)}
                                            </span>
                                            {item.label}
                                        </span>
                                        {item.isTag ? (
                                            <Tag color="green" className="m-0 px-3 py-1 font-medium w-fit">
                                                {item.value || "—"}
                                            </Tag>
                                        ) : (
                                            <span className="text-sm font-semibold text-gray-900">{item.value || "—"}</span>
                                        )}
                                    </div>
                                </Col>
                            ))}
                        </Row>
                    </Col>

                    {/* Commission Details Section */}
                    <Col span={24} className="border-b border-gray-200 py-4">
                        <div className="flex items-center gap-2 mb-4">
                            <DollarOutlined style={{ color: "var(--primary-color, #1890ff)", fontSize: "16px" }} />
                            <span style={{ fontSize: "15px", fontWeight: 600 }}>Commission Information</span>
                        </div>
                        <Row gutter={[24, 16]}>
                            {[
                                { label: "Commission Amount", value: `$${customerDetail?.commissionAmount || 0}`, isTag: true, tagColor: "green" },
                            ].map((item) => (
                                <Col span={12} key={item.label}>
                                    <div className="flex flex-col">
                                        <span className="text-xs text-gray-500 flex items-center gap-2 mb-1">
                                            <span style={{ color: "var(--primary-color, #1890ff)" }}>
                                                {getIconForField(item.label)}
                                            </span>
                                            {item.label}
                                        </span>
                                        {item.isTag ? (
                                            <Tag color={item.tagColor} className="m-0 px-3 py-1 font-medium w-fit">
                                                {item.value || "—"}
                                            </Tag>
                                        ) : (
                                            <span className="text-sm font-semibold text-gray-900">{item.value || "—"}</span>
                                        )}
                                    </div>
                                </Col>
                            ))}
                        </Row>
                    </Col>

                    {/* Affiliate Details Section */}
                    <Col span={24} className="py-4">
                        <div className="flex items-center gap-2 mb-4">
                            <InfoCircleOutlined style={{ color: "var(--primary-color, #1890ff)", fontSize: "16px" }} />
                            <span style={{ fontSize: "15px", fontWeight: 600 }}>Affiliate Information</span>
                        </div>
                        <Row gutter={[24, 16]}>
                            {[
                                { label: "Affiliate Name", value: customerDetail?.Affiliate?.[0]?.name },
                                { label: "Affiliate Email", value: customerDetail?.Affiliate?.[0]?.email },
                                { label: "Company Name", value: customerDetail?.Affiliate?.[0]?.companyName },
                                { label: "Commission Type", value: customerDetail?.Affiliate?.[0]?.commissionType },
                                { label: "Commission Value", value: `${customerDetail?.Affiliate?.[0]?.commissionValue}${customerDetail?.Affiliate?.[0]?.commissionType === 'percentage' ? '%' : '$'}`, isTag: true },
                            ].map((item) => (
                                <Col span={12} key={item.label}>
                                    <div className="flex flex-col">
                                        <span className="text-xs text-gray-500 flex items-center gap-2 mb-1">
                                            <span style={{ color: "var(--primary-color, #1890ff)" }}>
                                                {getIconForField(item.label)}
                                            </span>
                                            {item.label}
                                        </span>
                                        {item.isTag ? (
                                            <Tag color="blue" className="m-0 px-3 py-1 font-medium capitalize w-fit">
                                                {item.value || "—"}
                                            </Tag>
                                        ) : (
                                            <span className="text-sm font-semibold text-gray-900">{item.value || "—"}</span>
                                        )}
                                    </div>
                                </Col>
                            ))}
                        </Row>
                    </Col>
                </Row>
            </CommonCard>
        </div>
    );
};

export default EnhancedAffiliatesCustomerDetails;
